/** STRATURILE UNUI PERETE: din ce e făcut, strat cu strat, de la stânga la dreapta.
 *
 *  CE SE SCHIMBĂ FAȚĂ DE ÎNAINTE. Un zid avea UN material — `beton`, `zidarie`
 *  sau `polistiren` — și o grosime, iar asta ajunge pentru întrebarea pe care o
 *  pun motoarele: duce sau nu duce încărcare. Nu ajunge însă pentru planșă. Un
 *  perete de fațadă real e zidărie, termosistem, tencuială la exterior și
 *  gletuială la interior, iar detaliul ăla e chiar ce se desenează în secțiune.
 *  Deci `material` RĂMÂNE (portanța e a zidului, nu a stratului) și lângă el
 *  vine o listă de straturi, care e a desenului și a antemăsurătorii.
 *
 *  STÂNGA ȘI DREAPTA SE NUMESC O SINGURĂ DATĂ, AICI.
 *
 *  Un perete se desenează trăgând de la un punct la altul: de unde ai apăsat
 *  până unde ai dat drumul. Perechea aia se scrie în document ca `axis`, și ea e
 *  singurul lucru din geometria unui zid care are un SENS — poligonul n-are, un
 *  dreptunghi nu spune dinspre ce capăt a fost desenat. Deci straturile se
 *  numără pe ea: stânga e mâna stângă a celui care merge PE DESEN de la
 *  `axis[0]` spre `axis[1]`.
 *
 *  «PE DESEN» e partea care trebuie spusă cu voce tare. Pânza desenează cu
 *  `translate(tx ty) scale(k)` și fără răsturnare, deci y crește ÎN JOS, ca pe
 *  orice ecran. Într-un sistem cu y în jos, stânga celui care merge pe direcția
 *  `(dx, dy)` e `(dy, −dx)` — pe dos față de formula din manualul de geometrie,
 *  unde y crește în sus. Scrisă greșit, toate straturile ar fi ieșit în oglindă,
 *  iar greșeala s-ar fi văzut doar pe un perete asimetric, adică târziu.
 *
 *  `arcDinAx` (din `doc.ts`) offsetează spre `(−dy, dx)` pentru valori
 *  pozitive, adică spre DREAPTA în înțelesul de mai sus. De-aia stânga e
 *  offsetul NEGATIV, și de-aia constanta de mai jos există: ca semnul să fie
 *  scris o dată, cu motivul lângă el.
 *
 *  UN ZID FĂRĂ AX N-ARE STRATURI. Cele transcrise din planșă n-au `axis` —
 *  acolo nimeni n-a tras de nimic, sunt hașuri citite dintr-un DWG. Editorul
 *  scrie axul în clipa în care cineva pune primul strat (v.
 *  `model::adaugaStrat`), deci întrebarea nu se pune pentru un zid pe care chiar
 *  se lucrează; iar pentru unul care ar avea cumva straturi fără ax, desenul
 *  cade înapoi pe hașura de rol, ca până acum. O convenție inventată aici ar fi
 *  fost o stângă ghicită.
 *
 *  HAȘURA E TOT GEOMETRIE — v. `plan/hasura.ts` pentru de ce nu `<pattern>`. Ce
 *  se adaugă aici sunt două feluri de linie pe care planșa le cere și pe care
 *  hașura dreaptă nu le putea da: ZIGZAGUL vatei minerale și linia
 *  ÎNTRERUPTĂ a foliilor. Amândouă se construiesc PESTE segmentele deja tăiate
 *  la conturul stratului, deci tăierea rămâne una singură.
 *
 *  UNGHIURILE SUNT ALE PERETELUI, NU ALE LUMII. `HASURA_ROL` le are absolute —
 *  45° e 45° oriunde —, ceea ce e bine pentru un zid omogen: două ziduri lipite
 *  se citesc ca unul dacă hașura lor curge la fel. Pentru straturi e pe dos: o
 *  vată minerală trebuie să aibă undele DE-A LUNGUL ei, iar un gips-carton
 *  liniile paralele cu fața lui, altfel un perete vertical și unul orizontal
 *  arată ca două materiale diferite. Deci aici unghiul se adună cu cel al
 *  axului.
 */
import { arcDinAx, inInel } from "../doc";
import type { Perete, Pt, Strat } from "../doc";
import { caleHasura, liniiHasuraMulti } from "../../plan/hasura";
import { t } from "../../../i18n";
import type { Cheie } from "../../../i18n";

/** Spre ce parte duce offsetul pozitiv al lui `arcDinAx`: spre DREAPTA, fiindcă
 *  pe desen y crește în jos — v. nota de sus. Stânga e deci `-1`, iar
 *  straturile se așază pornind de la fața stângă. */
export const STANGA = -1;

/** Cum se desenează un strat. `unghiuri` și `pas` sunt ca la `FelHasura`, doar
 *  că unghiurile se măsoară FAȚĂ DE AXUL PERETELUI. */
export type HasuraStrat = {
  unghiuri: number[];
  pas: number;
  /** Amplitudinea zigzagului, în metri — de la linia dreaptă până la vârf.
   *  Lipsă = linii drepte. */
  zigzag?: number;
  /** Lungimea unei unde de zigzag. Lipsă = de patru ori amplitudinea. */
  unda?: number;
  /** Linie întreruptă: `[dungă, gol]`, în metri. Lipsă = linie continuă. */
  intrerupt?: [number, number];
};

export type FelStrat = {
  kind: string;
  /** Familia, pentru grupele din listă. */
  grupa: string;
  /** Grosimea cu care intră un strat nou de felul ăsta, în metri. E grosimea
   *  OBIȘNUITĂ din execuție, nu un minim: 25 cm de zidărie, 10 de termosistem,
   *  12,5 mm o placă de gips-carton. */
  grosime: number;
  hasura: HasuraStrat;
};

/** CATALOGUL DE STRATURI, în ordinea în care le arată panoul.
 *
 *  Sunt felurile care se pun de obicei într-un perete de bloc, plus cele două
 *  care se pun în zidurile de la apă (hidroizolația) și în cele de la exterior
 *  (bariera de vapori). Hașurile sunt din tabelul convențional de reprezentare
 *  în secțiune, cu aceeași regulă ca a lui `HASURA_ROL`: același unghi, alt
 *  PAS, deosebește materialele înrudite; unghiul invers deosebește materialele
 *  care se ating.
 *
 *  ACELEAȘI CHEI SUNT ÎN SCHEMA CATALOGULUI
 *  (`architecture/layout/catalog/tronsoane/tronsoane.schema.json`, `layers`),
 *  iar `test_layer_kinds_agree_with_the_editor` le ține egale: un strat pe care
 *  schema nu-l cunoaște nu oprește desenul, dar oprește publicarea modulului —
 *  adică se vede abia la sfârșit, la omul care nu l-a pus. */
export const FELURI_STRAT: FelStrat[] = [
  // --- ce duce încărcarea sau ține peretele în picioare ---
  { kind: "beton", grupa: "structura", grosime: 0.2,
    hasura: { unghiuri: [45], pas: 0.05 } },
  { kind: "caramida", grupa: "structura", grosime: 0.25,
    hasura: { unghiuri: [45], pas: 0.09 } },
  { kind: "bca", grupa: "structura", grosime: 0.25,
    hasura: { unghiuri: [45, 135], pas: 0.14 } },

  // --- ce oprește căldura și zgomotul ---
  // Vata are UNDE, polistirenul are linii: cele două stau de multe ori una
  // lângă alta în același perete, iar la 2 cm de desen o deosebire de pas nu
  // s-ar vedea.
  { kind: "vata_minerala", grupa: "izolatii", grosime: 0.1,
    hasura: { unghiuri: [0], pas: 0.025, zigzag: 0.007, unda: 0.03 } },
  { kind: "polistiren", grupa: "izolatii", grosime: 0.1,
    hasura: { unghiuri: [135], pas: 0.05 } },
  { kind: "gol_aer", grupa: "izolatii", grosime: 0.03,
    hasura: { unghiuri: [], pas: 1 } },

  // --- ce se vede și ce se atinge ---
  { kind: "tencuiala", grupa: "finisaje", grosime: 0.02,
    hasura: { unghiuri: [45], pas: 0.012, intrerupt: [0.012, 0.012] } },
  { kind: "gips_carton", grupa: "finisaje", grosime: 0.0125,
    hasura: { unghiuri: [0], pas: 0.005 } },
  { kind: "placaj", grupa: "finisaje", grosime: 0.01,
    hasura: { unghiuri: [45, 135], pas: 0.02 } },
  // FIBRA LEMNULUI e o linie continuă de-a lungul piesei. Întreruptă, la pasul
  // ăsta se citea ca un gard de scânduri puse de-a curmezișul.
  { kind: "lemn", grupa: "finisaje", grosime: 0.05,
    hasura: { unghiuri: [0], pas: 0.012 } },

  // --- foliile: sub un milimetru, deci pe planșă sunt o linie, nu o bandă ---
  { kind: "hidroizolatie", grupa: "bariere", grosime: 0.004,
    hasura: { unghiuri: [0], pas: 0.002 } },
  { kind: "bariera_vapori", grupa: "bariere", grosime: 0.001,
    hasura: { unghiuri: [0], pas: 0.002, intrerupt: [0.03, 0.015] } },
];

/** Felul unui strat, sau `null` când documentul poartă unul necunoscut. Un
 *  `kind` străin nu e o eroare de desen: se hașurează cu implicita și se arată
 *  ca atare, fiindcă un modul venit dintr-o versiune mai nouă a catalogului nu
 *  trebuie să facă desenul să tacă. */
export const felStrat = (kind: string): FelStrat | null =>
  FELURI_STRAT.find((f) => f.kind === kind) ?? null;

/** Hașura unui strat necunoscut: zidăria implicită, ca la pereți. */
export const HASURA_STRAT_IMPLICITA: HasuraStrat = { unghiuri: [45], pas: 0.14 };

export const hasuraStratului = (kind: string): HasuraStrat =>
  felStrat(kind)?.hasura ?? HASURA_STRAT_IMPLICITA;

/** Numele stratului, în limba ecranului — ca `numeFinisaj`: catalogul ține
 *  cheia tehnică, ecranul ia textul din `strat.<kind>`. */
export const numeStrat = (kind: string): string =>
  (felStrat(kind) ? t(`strat.${kind}` as Cheie) : kind);

export const numeGrupaStrat = (grupa: string): string =>
  t(`strat.grupa.${grupa}` as Cheie);

/** Grupele, în ordinea catalogului, fiecare cu felurile ei. */
export function grupeleStraturilor(): { grupa: string; feluri: FelStrat[] }[] {
  const out: { grupa: string; feluri: FelStrat[] }[] = [];
  for (const f of FELURI_STRAT) {
    const g = out.find((x) => x.grupa === f.grupa);
    if (g) g.feluri.push(f);
    else out.push({ grupa: f.grupa, feluri: [f] });
  }
  return out;
}

/** Cât ocupă straturile împreună — adică grosimea zidului stratificat. */
export const grosimeaStraturilor = (st: readonly Strat[]): number =>
  Math.round(st.reduce((s, x) => s + x.grosime, 0) * 1000) / 1000;

export type BandaStrat = {
  strat: Strat;
  /** Conturul benzii, în coordonatele desenului. */
  inel: Pt[];
  /** Unghiul axului peretelui, în grade — hașura se rotește cu el. */
  unghi: number;
};

/** BENZILE STRATURILOR: conturul fiecărui strat, de la stânga la dreapta.
 *
 *  Se construiesc DIN AX, nu tăind poligonul zidului, și asta e ce face să
 *  meargă la fel pe unul drept și pe unul curb: două fețe ale aceluiași arc,
 *  offsetat de două ori. Tăind poligonul cu semiplane, un zid curb ar fi ieșit
 *  cu straturi drepte prin el, iar unul în L (ocolind casa scării) s-ar fi rupt
 *  — o tăiere convexă nu știe de scobituri.
 *
 *  Lista e goală când zidul n-are straturi SAU n-are ax — v. nota de sus. */
export function benzileStraturilor(w: Perete): BandaStrat[] {
  if (!w.straturi.length || !w.ax) return [];
  const [a, b] = w.ax;
  const total = grosimeaStraturilor(w.straturi);
  const unghi = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  const out: BandaStrat[] = [];
  // Se pleacă de la fața din STÂNGA — offsetul cel mai depărtat în sensul
  // `STANGA` — și se coboară strat cu strat spre dreapta.
  let o = (STANGA * total) / 2;
  for (const strat of w.straturi) {
    const o2 = o - STANGA * strat.grosime;
    out.push({
      strat, unghi,
      inel: [...arcDinAx(a, b, w.bulge, o),
             ...arcDinAx(a, b, w.bulge, o2).reverse()],
    });
    o = o2;
  }
  return out;
}

/** Zigzagul unui segment: o polilinie care merge în sus și în jos de-a lungul
 *  lui, tăiată la conturul stratului.
 *
 *  TĂIEREA E PE VÂRFURI, NU PE INTERSECȚII, și dinadins: se păstrează numai
 *  bucățile cu AMÂNDOUĂ capetele în contur. Zigzagul iese lateral din linia
 *  dreaptă cu amplitudinea lui, deci o tăiere pe intersecții l-ar fi lăsat să
 *  scape din bandă exact la capete, unde stratul de alături începe — iar două
 *  hașuri care se încalecă se citesc ca un al treilea material. Cu regula asta,
 *  zigzagul rămâne mereu ÎNĂUNTRU; ce pierde e cel mult o jumătate de undă la
 *  fiecare capăt, ceea ce pe planșă nu se vede. */
function zigzagul(seg: number[], inel: readonly Pt[],
                  amp: number, unda: number): number[][] {
  const [x0, y0, x1, y1] = seg;
  const L = Math.hypot(x1 - x0, y1 - y0);
  if (!(L > 1e-9) || !(unda > 1e-6)) return [];
  const ux = (x1 - x0) / L;
  const uy = (y1 - y0) / L;
  const n = Math.max(1, Math.round((L / unda) * 2));   // două vârfuri pe undă
  const pas = L / n;
  const puncte: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    // Capetele stau PE linie, vârfurile alternează de-o parte și de alta: așa
    // undele se leagă cap la cap cu ale segmentului vecin.
    const s = i === 0 || i === n ? 0 : (i % 2 === 0 ? -amp : amp);
    puncte.push([x0 + ux * i * pas - uy * s, y0 + uy * i * pas + ux * s]);
  }
  const out: number[][] = [];
  for (let i = 0; i + 1 < puncte.length; i++) {
    const p = puncte[i];
    const q = puncte[i + 1];
    if (!inInel(p, inel) || !inInel(q, inel)) continue;
    out.push([p[0], p[1], q[0], q[1]]);
  }
  return out;
}

/** Un segment tăiat în dungi: `[dungă, gol]`.
 *
 *  DUNGILE SE DECALEAZĂ DIN LINIE ÎN LINIE, și fără asta desenul iese pe dos.
 *  Pornite toate din capătul segmentului, golurile cad unul sub altul și zeci
 *  de linii întrerupte se citesc ca dungi PERPENDICULARE pe ele — măsurat pe
 *  proba de mostre: lemnul, hașurat cu linii orizontale întrerupte la 6 cm,
 *  ieșea ca un gard de scânduri verticale. Deci fiecare a doua linie pleacă
 *  decalată cu o jumătate de perioadă, iar golurile nu mai formează coloane.
 *
 *  A CÂTA LINIE E se află din POZIȚIA ei, nu dintr-un indice: segmentele vin
 *  deja tăiate la contur și amestecate, dar fiecare știe pe ce bandă stă —
 *  proiecția lui pe normala hașurii, împărțită la pas. Aceeași linie dă mereu
 *  aceeași fază, deci desenul nu se schimbă de la o redesenare la alta. */
function intrerupt(seg: number[], dunga: number, gol: number,
                   pas: number, n: Pt): number[][] {
  const [x0, y0, x1, y1] = seg;
  const L = Math.hypot(x1 - x0, y1 - y0);
  const perioada = dunga + gol;
  if (!(L > 1e-9) || !(perioada > 1e-6)) return [];
  const ux = (x1 - x0) / L;
  const uy = (y1 - y0) / L;
  const banda = Math.round((x0 * n[0] + y0 * n[1]) / pas);
  const faza = Math.abs(banda) % 2 ? perioada / 2 : 0;
  const out: number[][] = [];
  for (let s = -faza; s < L; s += perioada) {
    const a = Math.max(s, 0);
    const e = Math.min(s + dunga, L);
    if (e - a < 1e-9) continue;
    out.push([x0 + ux * a, y0 + uy * a, x0 + ux * e, y0 + uy * e]);
  }
  return out;
}

/** Hașura unui strat, ca un singur `d` — v. `caleHasura` pentru de ce unul
 *  singur. Unghiurile se adună cu al peretelui, deci desenul unui strat se
 *  învârte cu zidul lui. */
export function caleaStratului(kind: string, inel: readonly Pt[],
                               unghiPerete: number): string {
  const h = hasuraStratului(kind);
  const inele = [inel.map((p) => [p[0], p[1]])];
  if (h.intrerupt) {
    const [d, g] = h.intrerupt;
    return caleHasura(h.unghiuri.flatMap((u) => {
      const t = ((u + unghiPerete) * Math.PI) / 180;
      // Normala hașurii, adică axa pe care se numără benzile — aceeași pe care
      // le așază `liniiHasuraMulti`.
      const n: Pt = [-Math.sin(t), Math.cos(t)];
      return liniiHasuraMulti(inele, h.pas, u + unghiPerete)
        .flatMap((s) => intrerupt(s, d, g, h.pas, n));
    }));
  }
  const drepte = h.unghiuri.flatMap((u) =>
    liniiHasuraMulti(inele, h.pas, u + unghiPerete));
  if (h.zigzag) {
    const unda = h.unda ?? h.zigzag * 4;
    return caleHasura(drepte.flatMap((s) => zigzagul(s, inel, h.zigzag!, unda)));
  }
  return caleHasura(drepte);
}
